/* VS HUMANO — global WebSocket Provider + hooks.
 *
 * The connection is owned at the App level (see <VsHumanProvider>) so it
 * SURVIVES navigation between the lobby (/vs-humano) and the duel screen
 * (/jogar/:id?mode=vs_human). Multiple components can subscribe to messages
 * independently.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

function wsUrl() {
  const api = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return api.replace(/^http/i, "ws") + "/api/vs-human/ws";
}

const VsHumanCtx = createContext({
  connected: false,
  userId: null,
  send: () => {},
  subscribe: () => () => {},
});

export function VsHumanProvider({ children }) {
  const wsRef = useRef(null);
  const subsRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    let alive = true;
    let pingInterval = null;
    let backoff = 500;

    const connect = () => {
      if (!alive) return;
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        if (!alive) return;
        setConnected(true);
        backoff = 500;
        pingInterval = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
        }, 15000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "welcome") setUserId(msg.user_id);
          subsRef.current.forEach((h) => {
            try { h(msg); } catch { /* ignore handler errors */ }
          });
        } catch { /* ignore parse errors */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
        if (alive) {
          setTimeout(connect, Math.min(backoff, 5000));
          backoff *= 2;
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };
    connect();
    return () => {
      alive = false;
      if (pingInterval) clearInterval(pingInterval);
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
    };
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  const subscribe = useCallback((handler) => {
    subsRef.current.add(handler);
    return () => subsRef.current.delete(handler);
  }, []);

  return (
    <VsHumanCtx.Provider value={{ connected, userId, send, subscribe }}>
      {children}
    </VsHumanCtx.Provider>
  );
}

export function useVsHumanContext() {
  return useContext(VsHumanCtx);
}

/**
 * Back-compat hook: subscribes onMessage as long as the component is mounted.
 * The handler can change between renders without re-subscribing.
 */
export function useVsHumanSocket(onMessage) {
  const ctx = useVsHumanContext();
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  useEffect(() => {
    return ctx.subscribe((msg) => {
      if (cbRef.current) cbRef.current(msg);
    });
  }, [ctx]);
  return { connected: ctx.connected, userId: ctx.userId, send: ctx.send };
}
